import type { DocumentNode, GraphQLSchema } from 'graphql';

import { Dimension1 } from '../../gql/graphql';
import { getVariableTypes } from '../graphql/type-utils';
import { HEALTH_CHECK_FRAGMENTS } from './fragments';

export const getHealthCheckId = (
  service: string,
  viewGroup: string,
  view: string
): string => {
  return `${service}:${viewGroup}:${view}`;
};

export interface HealthCheckResult {
  id: string;
  timestamp: string;
  service: string;
  viewGroup: string;
  view: string;
  status: 'success' | 'failure';
  responseTime: number;
  error?: {
    message: string;
    details: unknown;
  };
  data: Record<string, unknown>;
  isManualCheck: boolean;
  userEmail?: string;
}

type HealthCheckIdentity = {
  service: string;
  viewGroup: string;
  view: string;
  /** Optional link surfaced in Slack alerts; `null` suppresses the link. */
  customUrl?: string | null;
};

/** GraphQL-backed check — executed via `commonService.queryGoGql`. */
export type GraphQLHealthCheckConfig = HealthCheckIdentity & {
  query: (typeof HEALTH_CHECK_FRAGMENTS)[keyof typeof HEALTH_CHECK_FRAGMENTS];
  variables: Record<string, unknown>;
  request?: never;
};

/**
 * REST-backed check — the dash-meta runner GETs `url` and treats any 2xx
 * as success. Use this for upstream dependencies (mappers, company-mapper)
 * that expose `/ping`-style readiness probes.
 *
 * `url` may include `{ENV_VAR}` placeholders that the dash-meta runner
 * resolves at request time (e.g. `{COMPANY_MAPPER_URL}/ping`), so this
 * config stays usable across environments without leaking env values
 * into the shared lib consumed by the dashboard.
 *
 * `useAdminAuth` sends the same admin session the GraphQL checks use (as
 * both Bearer token and kratos cookie, matching the pattern proven in
 * company.resolver) — for endpoints behind the platform's auth (e.g. the
 * go-api deliverables proxy, company-mapper /search) that have no
 * unauthenticated probe.
 *
 * A `body` makes the probe a POST with a JSON payload — use a minimal,
 * side-effect-free request that exercises the endpoint's real code path
 * (e.g. an actual company search) rather than just its router.
 */
export type RestHealthCheckConfig = HealthCheckIdentity & {
  request: {
    url: string;
    useAdminAuth?: boolean;
    body?: Record<string, unknown>;
  };
  query?: never;
  variables?: never;
};

export type HealthCheckConfig =
  | GraphQLHealthCheckConfig
  | RestHealthCheckConfig;

export const isRestHealthCheck = (
  check: HealthCheckConfig
): check is RestHealthCheckConfig =>
  'request' in check && check.request != null;

/**
 * Builds valid `composition_v3` query variables for health checks. The exact
 * filter/taxonomy semantics don't matter here — the check only verifies the
 * endpoint returns composition data — so corrections/taxonomies/breakdown
 * levels use safe defaults.
 */
const compositionV3HealthCheckVariables = (
  entities: ReadonlyArray<{ EntID: number | string; EntType: string }>,
  dates: { start_date: string; end_date: string }
): Record<string, unknown> => ({
  entities,
  filters: { start_date: dates.start_date, end_date: dates.end_date },
  corrections: {
    with_weight: true,
    with_sample_weight: true,
    with_contingent_workers: false,
  },
  role_taxonomy: 0,
  skill_taxonomy: 0,
  breakdown_options: {
    geo_breakdown_level: 'REGION',
    rics_breakdown_level: 'RICS_K10',
    role_breakdown_level: 'ROLE_LVL_1',
    skill_breakdown_level: 'SKILL_LVL_1',
  },
});

export const HEALTH_CHECKS: HealthCheckConfig[] = [
  // Shared checks used across pages
  {
    service: 'Auth',
    query: HEALTH_CHECK_FRAGMENTS.Auth,
    variables: {
      ids: ['test-user-id'],
    },
    viewGroup: 'shared',
    view: 'auth',
    customUrl: null,
  },
  {
    service: 'SavedSets',
    query: HEALTH_CHECK_FRAGMENTS.SavedSets,
    variables: {},
    viewGroup: 'shared',
    view: 'filtering',
    customUrl: null,
  },
  {
    service: 'SharedSelectionList',
    query: HEALTH_CHECK_FRAGMENTS.SharedSelectionList,
    variables: {},
    viewGroup: 'shared',
    view: 'filtering',
    customUrl: null,
  },
  {
    service: 'MonthWeekIndex',
    query: HEALTH_CHECK_FRAGMENTS.MonthWeekIndex,
    variables: {},
    viewGroup: 'shared',
    view: 'filtering',
    customUrl: null,
  },
  {
    service: 'CompanyIndustrySelectionList',
    query: HEALTH_CHECK_FRAGMENTS.CompanyIndustrySelectionList,
    variables: {},
    viewGroup: 'shared',
    view: 'filtering',
    customUrl: null,
  },
  // Company overview checks
  {
    service: 'Entity',
    query: HEALTH_CHECK_FRAGMENTS.EntityV2,
    variables: {
      req: {
        entID: 1116644,
        entType: 'COMPANY',
        numCompetitors: 7,
      },
    },
    viewGroup: 'company',
    view: 'overview',
  },
  // {
  //   service: 'TalentDiscovery',
  //   query: HEALTH_CHECK_FRAGMENTS.TalentDiscovery,
  //   variables: {
  //     query: {
  //       company: {
  //         ids: [
  //           1116644, 388108, 854453, 797078, 163383, 191201, 17945589, 488842,
  //           155209, 1465480, 15884122,
  //         ],
  //         current: true,
  //         non_current: false,
  //       },
  //       custom_role: {
  //         current: true,
  //         non_current: false,
  //         taxonomyId: {
  //           ricsK50Id: '31',
  //         },
  //       },
  //     },
  //   },
  //   viewGroup: 'company',
  //   view: 'overview',
  // },

  {
    service: 'CompositionSummary',
    query: HEALTH_CHECK_FRAGMENTS.CompositionSummaryData,
    variables: compositionV3HealthCheckVariables(
      [{ EntID: 1116644, EntType: 'COMPANY' }],
      { start_date: '2024-03', end_date: '2025-03' }
    ),
    viewGroup: 'company',
    view: 'overview',
  },
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      filters: {
        company: [1116644],
        custom_role: { taxonomyId: { ricsK50Id: '31' } },
        provider: [0],
        start_date: '2023-07-29',
        end_date: '2024-07-29',
        metric_mode: 'expected_hires',
      },
      dim1: Dimension1.Company,
    },
    viewGroup: 'company',
    view: 'overview',
  },

  // Geography overview checks
  {
    service: 'MapCoords',
    query: HEALTH_CHECK_FRAGMENTS.GeographySummaryMapData,
    variables: {
      query: {
        country: [],
        msa: [{ granularity: 'MSA', id: 2 }],
        region: [],
      },
    },
    viewGroup: 'geography',
    view: 'overview',
  },
  // {
  //   service: 'TalentDiscovery',
  //   query: HEALTH_CHECK_FRAGMENTS.TalentDiscovery,
  //   variables: {
  //     query: {
  //       user_msa: [2],
  //     },
  //   },
  //   viewGroup: 'geography',
  //   view: 'overview',
  // },

  {
    service: 'Composition',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    variables: compositionV3HealthCheckVariables(
      [{ EntID: 2, EntType: 'MSA' }],
      { start_date: '2023-04', end_date: '2024-04' }
    ),
    viewGroup: 'geography',
    view: 'overview',
  },
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      dim1: Dimension1.Geography,
      filters: {
        end_date: '2024-07-29',
        metric_mode: 'expected_hires',
        msa: ['2'],
        provider: [0],
        start_date: '2023-07-29',
      },
    },
    viewGroup: 'geography',
    view: 'overview',
  },
  // Role overview checks
  {
    service: 'Entity',
    query: HEALTH_CHECK_FRAGMENTS.EntityV2,
    variables: {
      req: {
        entID: 107,
        entType: 'ROLE_K150',
      },
    },
    viewGroup: 'role',
    view: 'overview',
  },

  {
    service: 'Composition',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    variables: compositionV3HealthCheckVariables(
      [{ EntID: 107, EntType: 'ROLE_LVL_3' }],
      { start_date: '2023-04', end_date: '2024-04' }
    ),
    viewGroup: 'role',
    view: 'overview',
  },

  // Individual Discovery checks
  // {
  //   service: 'TalentDiscovery',
  //   query: HEALTH_CHECK_FRAGMENTS.TalentDiscoveryDetailed,
  //   variables: {
  //     query: {
  //       flight_risk: {
  //         current: true,
  //         ids: [3],
  //         non_current: false,
  //       },
  //       gender: [2],
  //       num_results: 5000,
  //       page: 1,
  //       user_msa: [199],
  //     },
  //   },
  //   viewGroup: 'individual',
  //   view: 'discovery',
  //   customUrl: 'talent-discovery',
  // },
  // Company compositions checks

  {
    service: 'Composition',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: '1116644', EntType: 'COMPANY' },
        { EntID: '919809', EntType: 'COMPANY' },
        { EntID: '1005273', EntType: 'COMPANY' },
        { EntID: '1010813', EntType: 'COMPANY' },
        { EntID: '256277', EntType: 'COMPANY' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    viewGroup: 'company',
    view: 'compositions',
  },
  // Geography compositions checks

  {
    service: 'Composition',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: 2, EntType: 'MSA' },
        { EntID: 1, EntType: 'MSA' },
        { EntID: 3, EntType: 'MSA' },
        { EntID: 21, EntType: 'MSA' },
        { EntID: 5, EntType: 'MSA' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    viewGroup: 'geography',
    view: 'compositions',
  },
  // Role compositions checks

  {
    service: 'Composition',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: 107, EntType: 'ROLE_LVL_3' },
        { EntID: 117, EntType: 'ROLE_LVL_3' },
        { EntID: 140, EntType: 'ROLE_LVL_3' },
        { EntID: 17, EntType: 'ROLE_LVL_3' },
        { EntID: 52, EntType: 'ROLE_LVL_3' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    viewGroup: 'role',
    view: 'compositions',
  },
  // Company transitions checks

  {
    service: 'Transitions',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV2,
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: {
          custom_role: {
            taxonomyId: {
              ricsK50Id: '31',
            },
          },
          end_date: '2024-04',
          start_date: '2008-01',
        },
      },
    },
    viewGroup: 'company',
    view: 'transitions',
  },
  // Geography transitions checks

  {
    service: 'Transitions',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV2,
    variables: {
      req: {
        entities: [{ EntID: 2, EntType: 'MSA' }],
        filters: {
          end_date: '2024-04',
          start_date: '2008-01',
        },
      },
    },
    viewGroup: 'geography',
    view: 'transitions',
  },
  // Role transitions checks

  {
    service: 'Transitions',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV2,
    variables: {
      req: {
        entities: [{ EntID: 107, EntType: 'ROLE_K150' }],
        filters: {
          end_date: '2024-04',
          start_date: '2008-01',
        },
      },
    },
    viewGroup: 'role',
    view: 'transitions',
  },
  // Company job postings checks
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      dim1: Dimension1.Company,
      filters: {
        company: ['1116644'],
        custom_role: {
          taxonomyId: {
            ricsK50Id: '31',
          },
        },
        end_date: '2024-07-29',
        metric_mode: 'posting',
        provider: [0],
        start_date: '2019-03-04',
      },
    },
    viewGroup: 'company',
    view: 'postings',
  },
  {
    service: 'PostingsRoles',
    query: HEALTH_CHECK_FRAGMENTS.PostingsRoles,
    variables: {
      dim1: Dimension1.Company,
      filters: {
        company: ['1116644'],
        custom_role: {
          taxonomyId: {
            ricsK50Id: '31',
          },
        },
        end_date: '2024-07-29',
        metric_mode: 'posting',
        provider: [0],
        start_date: '2019-03-04',
      },
    },
    viewGroup: 'company',
    view: 'postings',
  },
  // Geography job postings checks
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      dim1: Dimension1.Geography,
      filters: {
        end_date: '2024-07-29',
        metric_mode: 'posting',
        msa: ['2'],
        provider: [2],
        start_date: '2019-03-04',
      },
    },
    viewGroup: 'geography',
    view: 'postings',
  },
  {
    service: 'PostingsRoles',
    query: HEALTH_CHECK_FRAGMENTS.PostingsRoles,
    variables: {
      dim1: Dimension1.Geography,
      filters: {
        end_date: '2024-07-29',
        metric_mode: 'posting',
        msa: ['2'],
        provider: [2],
        start_date: '2019-03-04',
      },
    },
    viewGroup: 'geography',
    view: 'postings',
  },
  // Role job postings checks
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      dim1: Dimension1.Role,
      filters: {
        custom_role: {
          entities: [
            {
              id: '2',
              levelId: 'role_k150',
            },
          ],
          taxonomyId: {
            default: true,
          },
        },
        custom_skill: {
          taxonomy_id: 0,
          from_new_refs: true,
        },
        end_date: '2025-10-20',
        metric_mode: 'posting',
        provider: [0],
        start_date: '2022-03-08',
      },
    },
    viewGroup: 'role',
    view: 'postings',
  },
  // Mirrors company/postings + geography/postings — the role-postings page's
  // "Top Companies" chart renders via PostingsRoles (PostingDataProvider's
  // PostingTopQuery branch), so an outage here would silently break that chart.
  {
    service: 'PostingsRoles',
    query: HEALTH_CHECK_FRAGMENTS.PostingsRoles,
    variables: {
      dim1: Dimension1.Role,
      filters: {
        custom_role: {
          entities: [
            {
              id: '2',
              levelId: 'role_k150',
            },
          ],
          taxonomyId: {
            default: true,
          },
        },
        end_date: '2025-10-20',
        metric_mode: 'posting',
        provider: [0],
        start_date: '2022-03-08',
      },
    },
    viewGroup: 'role',
    view: 'postings',
  },

  // Company pay checks
  {
    service: 'Compensation',
    query: HEALTH_CHECK_FRAGMENTS.Compensation,
    variables: {
      dim1: Dimension1.Company,
      dim2: 'all',
      filters: {
        company: ['1116644'],
        custom_role: {
          taxonomyId: {
            ricsK50Id: '31',
          },
        },
      },
    },
    viewGroup: 'company',
    view: 'pay',
  },
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      dim1: Dimension1.Company,
      filters: {
        company: ['1116644'],
        custom_role: {
          taxonomyId: {
            ricsK50Id: '31',
          },
        },
        end_date: '2024-07-29',
        provider: [4, 2, 3],
        start_date: '2009-01-01',
      },
    },
    viewGroup: 'company',
    view: 'pay',
  },
  // Geography pay checks
  {
    service: 'Compensation',
    query: HEALTH_CHECK_FRAGMENTS.Compensation,
    variables: {
      dim1: Dimension1.Geography,
      dim2: 'all',
      filters: {
        msa: ['2'],
      },
    },
    viewGroup: 'geography',
    view: 'pay',
  },
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      dim1: Dimension1.Geography,
      filters: {
        end_date: '2024-07-29',
        msa: ['2'],
        provider: [4, 2, 3],
        start_date: '2009-01-01',
      },
    },
    viewGroup: 'geography',
    view: 'pay',
  },
  // Role pay checks
  {
    service: 'Compensation',
    query: HEALTH_CHECK_FRAGMENTS.Compensation,
    variables: {
      dim1: Dimension1.Role,
      dim2: 'all',
      filters: {
        role_k150: ['107'],
      },
    },
    viewGroup: 'role',
    view: 'pay',
  },
  {
    service: 'Postings',
    query: HEALTH_CHECK_FRAGMENTS.Postings,
    variables: {
      dim1: Dimension1.Role,
      filters: {
        end_date: '2024-07-29',
        provider: [4, 2, 3],
        custom_role: {
          entities: [
            { id: '2', levelId: 'role_k150' },
            { id: '1', levelId: 'role_k150' },
          ],
          taxonomyId: { default: true },
        },
        start_date: '2009-01-01',
      },
    },
    viewGroup: 'role',
    view: 'pay',
  },
  // Company sentiment checks
  {
    service: 'Sentiment',
    query: HEALTH_CHECK_FRAGMENTS.Sentiment,
    variables: {
      dim1: Dimension1.Company,
      filters: {
        company: ['1116644', '919809', '1005273', '1010813', '256277'],
        custom_role: {
          taxonomyId: {
            ricsK50Id: '31',
          },
        },
        end_date: '2024-04',
        start_date: '2020-01',
      },
    },
    viewGroup: 'company',
    view: 'sentiment',
  },
  // Geography sentiment checks
  {
    service: 'Sentiment',
    query: HEALTH_CHECK_FRAGMENTS.Sentiment,
    variables: {
      dim1: Dimension1.Geography,
      filters: {
        end_date: '2024-04',
        msa: ['2', '1', '3', '21', '5'],
        start_date: '2020-01',
      },
    },
    viewGroup: 'geography',
    view: 'sentiment',
  },
  // Role sentiment checks
  {
    service: 'Sentiment',
    query: HEALTH_CHECK_FRAGMENTS.Sentiment,
    variables: {
      dim1: Dimension1.Role,
      filters: {
        custom_role: {
          entities: [
            {
              id: '2',
              levelId: 'role_k150',
            },
          ],
          taxonomyId: {
            default: true,
          },
        },
        end_date: '2025-09',
        start_date: '2025-09',
      },
    },
    viewGroup: 'role',
    view: 'sentiment',
  },
  // Company discovery checks
  {
    service: 'Screener',
    query: HEALTH_CHECK_FRAGMENTS.Screener,
    variables: {
      filters: {
        dim: 'COMPANY',
        entity_info_selectors: {},
        contingent_workers: false,
        pagination: {
          limit: 35,
          offset: 0,
        },
        primary_selector: {},
        segments: {
          attrition: [
            {
              end_val: 0.25,
              filter: {},
              start_val: 0,
            },
          ],
          headcount: [
            {
              end_val: 10000,
              filter: {
                region: [14],
              },
              start_val: 0,
            },
          ],
        },
      },
    },
    viewGroup: 'company',
    view: 'discovery',
  },
  // Geography discovery checks
  {
    service: 'Screener',
    query: HEALTH_CHECK_FRAGMENTS.Screener,
    variables: {
      filters: {
        dim: 'GEOGRAPHY',
        entity_info_selectors: {},
        contingent_workers: false,
        pagination: {
          limit: 35,
          offset: 0,
        },
        primary_selector: {},
        segments: {
          attrition: [
            {
              end_val: 0.25,
              filter: {},
              start_val: 0,
            },
          ],
          growth_yoy: [
            {
              end_val: 0.25,
              filter: {
                gender: [1],
              },
              start_val: 0,
            },
          ],
        },
      },
    },
    viewGroup: 'geography',
    view: 'discovery',
  },
  // Role discovery checks
  {
    service: 'Screener',
    query: HEALTH_CHECK_FRAGMENTS.Screener,
    variables: {
      filters: {
        dim: 'ROLE',
        entity_info_selectors: {},
        contingent_workers: false,
        pagination: {
          limit: 35,
          offset: 0,
        },
        primary_selector: {},
        segments: {
          tenure: [
            {
              end_val: 2,
              filter: {},
              start_val: 0,
            },
          ],
        },
      },
    },
    viewGroup: 'role',
    view: 'discovery',
  },
  // Reports builder checks
  {
    service: 'ReportsValidation',
    query: HEALTH_CHECK_FRAGMENTS.ReportConfigHealthCheck,
    variables: {},
    viewGroup: 'deliverables',
    view: 'report-builder',
    customUrl: 'report-builder/report_type',
  },
  // Reports view checks
  {
    service: 'Users',
    query: HEALTH_CHECK_FRAGMENTS.UserSearchHealthCheck,
    variables: {
      input: 'revelio',
    },
    viewGroup: 'deliverables',
    view: 'report-builder',
    customUrl: '/reports',
  },
  // Talent Discovery V3 checks
  {
    service: 'TalentDiscoveryV3',
    query: HEALTH_CHECK_FRAGMENTS.TalentDiscoveryV3,
    variables: {
      query: {
        filters: {
          expr: {
            user_and: {
              children: [
                {
                  user_value: {
                    child: {
                      txt: {
                        user_attribute: {
                          kind: 'DESC',
                          text: 'engineer',
                        },
                      },
                    },
                  },
                },
                {
                  user_value: {
                    child: {
                      txt: {
                        user_attribute: {
                          kind: 'DESC',
                          text: 'software',
                        },
                      },
                    },
                  },
                },
                {
                  user_value: {
                    child: {
                      val: {
                        user_attribute: {
                          id: 1,
                          kind: 'SKILL_LVL_1',
                        },
                      },
                    },
                  },
                },
                {
                  user_value: {
                    child: {
                      val: {
                        user_attribute: {
                          id: 8,
                          kind: 'SKILL_LVL_1',
                        },
                      },
                    },
                  },
                },
                {
                  user_value: {
                    child: {
                      val: {
                        user_attribute: {
                          id: 1,
                          kind: 'GENDER',
                        },
                      },
                    },
                  },
                },
                {
                  user_value: {
                    child: {
                      val: {
                        user_attribute: {
                          id: 2,
                          kind: 'ETHNICITY',
                        },
                      },
                    },
                  },
                },
              ],
            },
          },
          roles_tax: 'default',
          skills_tax: 0,
        },
        pagination: {
          page_num: 1,
          page_size: 5000,
        },
      },
    },
    viewGroup: 'talent-discovery',
    view: 'v3',
  },

  // --- Upstream REST dependencies ---
  // These services back the AI-search flows. Each host below mirrors the
  // URL the dash-meta runtime actually hits — see
  //   apps/dash-meta/src/ai-search/utils/{skill,location,role}-mapper-api.ts
  // — so a green probe is evidence the dependency users hit is up (the
  // runtime URLs are hardcoded there, not env-driven). Company-mapper has
  // no unauthenticated probe (`/ping` 401s), so its coverage is the
  // authenticated CompanySearch check at the bottom of this list.
  {
    service: 'SkillMapper',
    viewGroup: 'upstream',
    view: 'skill-mapper',
    customUrl: null,
    request: {
      url: 'https://skill-mapping.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/ping',
    },
  },
  {
    service: 'LocationMapper',
    viewGroup: 'upstream',
    view: 'location-mapper',
    customUrl: null,
    request: {
      url: 'https://location-mapper.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/ping',
    },
  },
  {
    service: 'RoleMapper',
    viewGroup: 'upstream',
    view: 'role-mapper',
    customUrl: null,
    request: {
      url: 'https://role-mapping.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/ping',
    },
  },
  // Ory Kratos — every login and session check flows through it; if it's
  // down, nobody can sign in. /health/alive is Ory's standard public probe.
  {
    service: 'Kratos',
    viewGroup: 'upstream',
    view: 'kratos',
    customUrl: null,
    request: {
      url: '{ORY_KRATOS_URL}/health/alive',
    },
  },
  // OnlyOffice DocSpace backs deliverables document viewing/editing.
  {
    service: 'OnlyOffice',
    viewGroup: 'upstream',
    view: 'onlyoffice',
    customUrl: null,
    request: {
      url: '{ONLYOFFICE_BASE_URL}/healthcheck',
    },
  },
  // The account-portal backend (user-mgmt app) — account settings, seats,
  // billing. Its root route is the lightest liveness signal it exposes.
  {
    service: 'AccountPortalApi',
    viewGroup: 'upstream',
    view: 'account-portal',
    customUrl: null,
    request: {
      url: '{ACCOUNT_PORTAL_BACKEND_ROOT}/api',
    },
  },
  // The DataBuilder v2 backend, proxied through the go-api host. A separate
  // upstream from GraphQL — it can be down while every GraphQL check is
  // green. GET / is what DataBuilderRouteGuard fires on page load; the proxy
  // rejects anonymous requests, so reuse the admin auth the GraphQL checks
  // already hold.
  {
    service: 'DataBuilderApi',
    viewGroup: 'upstream',
    view: 'data-builder-api',
    customUrl: null,
    request: {
      url: '{GO_API_ROOT}/proxy/deliverables/',
      useAdminAuth: true,
    },
  },
  // Functional company search — the same POST /search call the dashboard
  // fires for company validation/lookup (see useValidateCompanies.ts and
  // useCompanyRics.ts). Goes beyond the host /ping above: a green result
  // means the search index itself answered a real query.
  {
    service: 'CompanySearch',
    viewGroup: 'upstream',
    view: 'company-search',
    customUrl: null,
    request: {
      url: '{COMPANY_MAPPER_URL}/search',
      useAdminAuth: true,
      body: {
        name: 'openai',
        employee_cnt_min: 1,
      },
    },
  },
];

export async function getHealthCheckVariableTypes(
  query: DocumentNode,
  schema: GraphQLSchema
): Promise<string> {
  return getVariableTypes(query, schema);
}
